import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "../lib/api";

const STORAGE_KEY = "alta_utm";
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "click_id"];

/**
 * Read utm_* params from the current URL, persist the last-touch UTM
 * (7-day sliding window), and log a click server-side once per page load.
 * Returns the stored utm blob (may be {}) so callers can display attribution.
 */
export function useUtmCapture() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const incoming = {};
    UTM_FIELDS.forEach((f) => {
      const v = params.get(f);
      if (v) incoming[f] = v.trim().slice(0, 200);
    });

    if (Object.keys(incoming).length === 0) return;

    // Persist as last-touch with 7-day expiry.
    const blob = {
      ...incoming,
      captured_at: Date.now(),
      landing_path: pathname,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
    } catch { /* ignore quota */ }

    // Fire-and-forget click log (server sanitises + drops if empty).
    apiClient.post("/utm/click", {
      ...incoming,
      landing_path: pathname,
      referrer: typeof document !== "undefined" ? document.referrer : "",
    }).catch(() => {});
  }, [pathname, search]);
}

export function getStoredUtm() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const blob = JSON.parse(raw);
    // 7-day sliding window
    if (!blob.captured_at || Date.now() - blob.captured_at > 7 * 24 * 3600 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const out = {};
    UTM_FIELDS.forEach((f) => { if (blob[f]) out[f] = blob[f]; });
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}
